"use client";

import { useEffect, useRef, useState } from "react";

/**
 * O filtro das clínicas da Arena (16/10/2026, Rodrigo): menina ou menino e o
 * ano de nascimento, e a página mostra só as turmas que servem.
 *
 * Começa vazio e não esconde nada: quem só quer olhar vê tudo, como antes.
 * Os cartões continuam sendo montados no servidor; este componente só liga e
 * desliga o `hidden` de quem carrega `data-g` e `data-anos` dentro de
 * `#clinicas`, e esconde o dia (`data-dia`) que ficar sem turma.
 *
 * A resposta vai para a URL (`?g=f&ano=2013`), para um link já filtrado poder
 * ser mandado a uma família. A régua é a faixa NORMAL da turma: jogar acima
 * passa por revisão de gente, e por isso fica fora do filtro (vira uma frase).
 */

type Genero = "F" | "M";

function leDaUrl(anos: number[]): { g: Genero | null; ano: number | null } {
  const p = new URLSearchParams(window.location.search);
  const g = p.get("g")?.toUpperCase();
  const ano = Number(p.get("ano"));
  return {
    g: g === "F" || g === "M" ? g : null,
    ano: anos.includes(ano) ? ano : null,
  };
}

function gravaNaUrl(g: Genero | null, ano: number | null) {
  const url = new URL(window.location.href);
  if (g) url.searchParams.set("g", g.toLowerCase());
  else url.searchParams.delete("g");
  if (ano) url.searchParams.set("ano", String(ano));
  else url.searchParams.delete("ano");
  window.history.replaceState(null, "", url);
}

export default function FiltroDasClinicas({
  anos,
  generos,
  duvida,
  children,
}: {
  anos: number[];
  generos: Genero[];
  duvida: string | null;
  /** Os atalhos por faixa (31/10/2026). Com `data-g`/`data-anos`, o filtro também os esconde. */
  children?: React.ReactNode;
}) {
  const [g, setG] = useState<Genero | null>(null);
  const [ano, setAno] = useState<number | null>(null);
  const [visiveis, setVisiveis] = useState<number | null>(null);
  // Quem escolhe o ano na mão vai direto para o cartão da faixa dela (31/10/2026).
  // O ano que chega pela URL não rola a página.
  const pular = useRef(false);

  // A URL só existe no navegador: o filtro de um link chega depois da montagem.
  useEffect(() => {
    const daUrl = leDaUrl(anos);
    /* eslint-disable react-hooks/set-state-in-effect -- sincroniza com a URL uma vez, na montagem */
    setG(daUrl.g);
    setAno(daUrl.ano);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [anos]);

  useEffect(() => {
    const raiz = document.getElementById("clinicas");
    if (!raiz) return;
    let n = 0;
    raiz.querySelectorAll<HTMLElement>("[data-g]").forEach((el) => {
      const anosDoEl = (el.dataset.anos ?? "").split(" ").map(Number);
      const serve =
        (!g || el.dataset.g === g) && (!ano || anosDoEl.includes(ano));
      el.hidden = !serve;
      if (serve && el.hasAttribute("data-turma")) n++;
    });
    raiz.querySelectorAll<HTMLElement>("[data-dia]").forEach((dia) => {
      dia.hidden =
        !!(g || ano) && !dia.querySelector("[data-turma]:not([hidden])");
    });
    /* eslint-disable-next-line react-hooks/set-state-in-effect -- a contagem vem do DOM que este efeito acabou de filtrar */
    setVisiveis(g || ano ? n : null);
    if (pular.current) {
      pular.current = false;
      raiz.querySelector<HTMLElement>("[data-turma]:not([hidden])")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [g, ano]);

  function escolhe(novoG: Genero | null, novoAno: number | null, pularParaOCartao = false) {
    pular.current = pularParaOCartao;
    setG(novoG);
    setAno(novoAno);
    gravaNaUrl(novoG, novoAno);
  }

  const chip = (ativo: boolean) =>
    `inline-flex min-h-[44px] items-center rounded-full border-2 px-5 text-[16px] font-extrabold ${
      ativo
        ? "border-[#002868] bg-[#002868] text-white"
        : "border-[#c9d4ea] bg-white text-[#002868]"
    }`;

  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-[#f5f7fb] p-4 md:gap-4 md:p-5">
      <span className="text-[18px] font-extrabold leading-snug">
        Quais clínicas são para você?
      </span>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Menina ou menino"
        >
          {generos.includes("F") && (
            <button
              type="button"
              aria-pressed={g === "F"}
              className={chip(g === "F")}
              onClick={() => escolhe(g === "F" ? null : "F", ano)}
            >
              Menina
            </button>
          )}
          {generos.includes("M") && (
            <button
              type="button"
              aria-pressed={g === "M"}
              className={chip(g === "M")}
              onClick={() => escolhe(g === "M" ? null : "M", ano)}
            >
              Menino
            </button>
          )}
        </div>
        <label className="flex flex-col gap-1 text-[14px] font-bold text-[#4a5669]">
          Ano de nascimento
          <select
            value={ano ?? ""}
            onChange={(e) =>
              escolhe(
                g,
                e.target.value ? Number(e.target.value) : null,
                !!e.target.value,
              )
            }
            className="min-h-[44px] rounded-[10px] border-2 border-[#c9d4ea] bg-white px-3 text-[16px] font-bold text-[#1b2433]"
          >
            <option value="">Escolha o ano</option>
            {anos.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
      </div>
      {children && <div className="flex flex-col gap-2">{children}</div>}
      <div aria-live="polite" className="flex flex-col gap-2">
        {visiveis !== null && (
          <>
            <p className="m-0 text-[16px] leading-snug text-[#1b2433]">
              {/* Cada cartão é uma FAIXA com várias opções, não uma clínica (21/10/2026). */}
              {visiveis === 0
                ? "Nenhuma faixa serve para essa combinação."
                : visiveis === 1
                  ? "Estas são as opções da faixa dela. As outras faixas estão escondidas."
                  : `Estas são as ${visiveis} faixas que servem para ela. As outras estão escondidas.`}{" "}
              <button
                type="button"
                onClick={() => escolhe(null, null)}
                className="font-bold text-[#002868] underline underline-offset-4"
              >
                Mostrar todas
              </button>
            </p>
            {duvida && (
              <p className="m-0 text-[14px] leading-snug text-[#4a5669]">
                Joga numa categoria acima da sua idade?{" "}
                <a
                  href={duvida}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-[#002868] underline underline-offset-4"
                >
                  Fala com a gente no WhatsApp
                </a>{" "}
                que a equipe confere.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
